import React, { useState, useRef, useMemo } from 'react';
import axios from 'axios';
import JournalEntryPanel from './JournalEntryPanel';
import Glossary from './Glossary';
import { IconCamera, IconHeart, IconX, IconChevron, IconPlus } from './Icons';
import { Fragrance, Ingredient, JournalEntry } from '../App';
import { computeFragranceCategory, SCENT_CATEGORIES, ScentCategory } from '../archetypes';
import { guessNoteFamily, computeCompatScore, computeSignalModifier, buildLikedNotes } from '../utils/scoring';

const API_URL = 'https://7b3t8n3es9.execute-api.us-east-1.amazonaws.com/prod';

interface ScentSignals {
  preferredDescriptors?: string[];
  avoidedDescriptors?: string[];
  avoidedFamilies?: string[];
  preferredSeasons?: string[];
}

interface DiscoverProps {
  userId: string;
  authToken: string;
  onContribution?: () => void;
  catalog: Fragrance[];
  catalogLoading: boolean;
  ingredients: Ingredient[];
  wishlistIds: Set<string>;
  journalIds: Set<string>;
  journalEntries: JournalEntry[];
  topFamilies: Array<{ category: string; pct: number }>;
  scentSignals: ScentSignals | null;
  noteSentimentMap: Record<string, number> | null;
  onWishlistToggle: (fragranceId: string, fragranceName: string, fragranceHouse: string) => void;
  onJournalSaved: () => void;
  onCatalogUpdated: () => void;
}


interface CompatResult {
  score: number;
  familyMatches: Array<{ family: string; notes: string[] }>;
  likedNoteMatches: string[];
  unknownNotes: string[];
}


const computeCompatBreakdown = (
  frag: Fragrance,
  topFamilies: Array<{ category: string; pct: number }>,
  likedNotes: Map<string, number>
): CompatResult | null => {
  if (!topFamilies || topFamilies.length === 0) return null;
  const allNotes = Array.from(new Set([
    ...(frag.notes || []),
    ...(frag.topNotes || []),
    ...(frag.middleNotes || []),
    ...(frag.baseNotes || []),
  ]));
  if (allNotes.length === 0) return null;

  let score = 0;
  let maxScore = 0;
  const familyBuckets = new Map<string, string[]>();
  const likedMatches: string[] = [];
  const unknowns: string[] = [];

  for (const note of allNotes) {
    const family = guessNoteFamily(note);
    if (family !== null) {
      const rank = topFamilies.findIndex(f => f.category === family);
      const familyPts = rank === 0 ? 3 : rank === 1 ? 2 : rank === 2 ? 1 : 0;
      score += familyPts;
      maxScore += 3;
      if (rank >= 0) {
        if (!familyBuckets.has(family)) familyBuckets.set(family, []);
        familyBuckets.get(family)!.push(note);
      }
    } else {
      unknowns.push(note);
    }
    const noteWeight = likedNotes.get(note.toLowerCase()) || 0;
    if (noteWeight > 0) {
      score += Math.min(noteWeight, 2);
      maxScore += 2;
      likedMatches.push(note);
    }
  }

  if (maxScore === 0) return null;
  const finalScore = Math.round(Math.max(0, Math.min(100, (score / maxScore) * 100)));
  const familyMatches = Array.from(familyBuckets.entries())
    .map(([family, notes]) => ({ family, notes }))
    .sort((a, b) => {
      const ra = topFamilies.findIndex(f => f.category === a.family);
      const rb = topFamilies.findIndex(f => f.category === b.family);
      return ra - rb;
    });
  return { score: finalScore, familyMatches, likedNoteMatches: likedMatches, unknownNotes: unknowns };
};

const ABBREVIATIONS: Record<string, string[]> = {
  'ysl': ['yves saint laurent', 'saint laurent'],
  'mfk': ['maison francis kurkdjian'],
  'cdg': ['comme des garcons', 'comme des garçons'],
  'tf': ['tom ford'],
  'jo malone': ['jo malone london'],
  'mm': ['maison margiela'],
  'pdm': ['parfums de marly'],
  'adp': ['acqua di parma'],
};

const normalize = (str: string): string =>
  str.toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/\s*&\s*/g, ' and ')
    .replace(/'/g, '').replace(/'/g, '')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const expandQuery = (q: string): string[] => {
  const n = normalize(q);
  const expansions = [n];
  for (const [abbr, list] of Object.entries(ABBREVIATIONS)) {
    if (n === abbr || n.startsWith(abbr + ' ')) {
      list.forEach(exp => expansions.push(n.replace(abbr, exp)));
    }
    list.forEach(exp => {
      if (n.includes(exp)) expansions.push(n.replace(exp, abbr));
    });
  }
  return Array.from(new Set(expansions));
};

const wordPrefixMatch = (target: string, q: string): boolean => {
  if (target.includes(q)) return true;
  const tWords = target.split(/\s+/);
  const qWords = q.split(/\s+/).filter(w => w.length > 1);
  if (qWords.length < 2) return false;
  return qWords.every(w => tWords.some(tw => tw.startsWith(w)));
};

const matchesFragrance = (f: Fragrance, queries: string[]): boolean => {
  const name = normalize(f.name || '');
  const house = normalize(f.house || '');
  const notes = [...(f.notes || []), ...(f.topNotes || []), ...(f.middleNotes || []), ...(f.baseNotes || [])].map(normalize);
  return queries.some(q =>
    wordPrefixMatch(name, q) || wordPrefixMatch(house, q) || notes.some(n => wordPrefixMatch(n, q))
  );
};

// House → region mapping (lowercase keys for matching)
const HOUSE_REGIONS: Record<string, string> = {
  // France
  'chanel': 'France', 'christian dior': 'France', 'dior': 'France', 'parfums christian dior': 'France',
  'guerlain': 'France', 'hermes': 'France', 'hermès': 'France',
  'yves saint laurent': 'France', 'saint laurent': 'France', 'givenchy': 'France',
  'cartier': 'France', 'jean paul gaultier': 'France', 'thierry mugler': 'France',
  'mugler': 'France', 'serge lutens': 'France', 'maison francis kurkdjian': 'France',
  "etat libre d'orange": 'France', 'ex nihilo': 'France', 'memo paris': 'France',
  'caron': 'France', 'diptyque': 'France', 'maison margiela': 'France',
  'comme des garcons': 'France', 'comme des garçons': 'France',
  'lalique': 'France', 'bdk parfums': 'France', 'juliette has a gun': 'France',
  'atelier cologne': 'France', "l'artisan parfumeur": 'France',
  'parfums de marly': 'France', 'initio': 'France',
  'histoires de parfums': 'France', 'robert piguet': 'France',
  'ann gerard': 'France', 'nicolas de barry': 'France', 'olibere': 'France',
  'phaedon': 'France',
  'galimard': 'France', 'molinard': 'France', 'fragonard': 'France',
  'annick goutal': 'France', 'houbigant': 'France', 'frapin': 'France',
  'cire trudon': 'France', 'by kilian': 'France', 'kilian': 'France',
  'frédéric malle': 'France', 'frederic malle': 'France',
  'essential parfums': 'France', 'liquides imaginaires': 'France',
  'maison crivelli': 'France', 'mancera': 'France',
  'matière première': 'France', 'matiere premiere': 'France',
  'mémoire archives': 'France', 'memoire archives': 'France',
  'montale': 'France', 'nina ricci': 'France', 'olfactive studio': 'France',
  'oriza l. legrand': 'France', 'parfumerie générale': 'France', 'parfumerie generale': 'France',
  'parfums mdci': 'France', 'parfums de nicolaï': 'France', 'parfums de nicolai': 'France',
  'paco rabanne': 'France', 'rabanne': 'France', 'rochas': 'France',
  'room 1015': 'France', 'stephane humbert lucas': 'France', 'stéphane humbert lucas': 'France',
  'the different company': 'France', 'van cleef & arpels': 'France', 'van cleef and arpels': 'France',
  'worth': 'France', 'lancôme': 'France', 'lancome': 'France', 'lanvin': 'France',
  'lubin': 'France', 'louis vuitton': 'France', 'kenzo': 'France', 'boucheron': 'France',
  'balenciaga': 'France', 'céline': 'France', 'celine': 'France',
  'dusita': 'France', 'marc-antoine barrois': 'France',
  'chloé': 'France', 'chloe': 'France',
  'neela vermeire créations': 'France', 'neela vermeire creations': 'France',
  'atelier des ors': 'France', 'akro': 'France',
  'perris monte carlo': 'France', 'james heeley': 'France',
  'anne sophie behaghel': 'France',
  // Italy
  'acqua di parma': 'Italy', 'armani': 'Italy', 'giorgio armani': 'Italy',
  'bvlgari': 'Italy', 'bulgari': 'Italy', 'dolce and gabbana': 'Italy',
  'dolce & gabbana': 'Italy', 'prada': 'Italy', 'versace': 'Italy',
  'valentino': 'Italy', 'gucci': 'Italy', 'salvatore ferragamo': 'Italy',
  'roberto cavalli': 'Italy', 'nobile 1942': 'Italy', 'profumum roma': 'Italy',
  'xerjoff': 'Italy', 'filippo sorcinelli': 'Italy',
  'mario valentino': 'Italy', 'nasomatto': 'Italy', 'pan draco': 'Italy',
  'hilde soliani': 'Italy', 'laboratorio olfattivo': 'Italy', 'bogue profumo': 'Italy',
  'bottega veneta': 'Italy', 'sospiro perfumes': 'Italy', 'tiziana terenzi': 'Italy',
  'moresque': 'Italy', 'the house of oud': 'Italy', 'naso profumi': 'Italy',
  'diesel': 'Italy', 'orto parisi': 'Italy',
  // United Kingdom
  'jo malone': 'United Kingdom', 'jo malone london': 'United Kingdom',
  "penhaligon's": 'United Kingdom', 'penhaligons': 'United Kingdom',
  'floris': 'United Kingdom', 'molton brown': 'United Kingdom',
  'creed': 'United Kingdom', 'laboratory perfumes': 'United Kingdom',
  'escentric molecules': 'United Kingdom', '4160 tuesdays': 'United Kingdom',
  'clive christian': 'United Kingdom', 'ffern': 'United Kingdom',
  'burberry': 'United Kingdom', 'alexander mcqueen': 'United Kingdom',
  'punk and dandy': 'United States', 'punk & dandy': 'United States',
  'beaufort london': 'United Kingdom', 'czech & speake': 'United Kingdom',
  'dunhill': 'United Kingdom', 'electimuss': 'United Kingdom',
  'jimmy choo': 'United Kingdom', 'jo loves': 'United Kingdom',
  'jorum studio': 'United Kingdom', 'lush': 'United Kingdom',
  'miller harris': 'United Kingdom', 'odette toilette': 'United Kingdom',
  'ormonde jayne': 'United Kingdom', 'papillon artisan perfumes': 'United Kingdom',
  'pell wall': 'United Kingdom', 'roja dove': 'United Kingdom', 'roja parfums': 'United Kingdom',
  'elizabeth arden': 'United Kingdom',
  // Middle East
  'amouage': 'Middle East', 'arabian oud': 'Middle East', 'ajmal': 'Middle East',
  'rasasi': 'Middle East', 'swiss arabian': 'Middle East',
  'lattafa': 'Middle East', 'lattafa perfumes': 'Middle East', 'dehn el oud': 'Middle East',
  'nabeel': 'Middle East', 'orientica': 'Middle East', 'zimaya': 'Middle East',
  'oud elite': 'Middle East', 'afnan': 'Middle East', 'fragrance world': 'Middle East',
  'al haramain': 'Middle East', 'nusuk': 'Middle East',
  'abdul samad al qurashi': 'Middle East', 'ahmed al maghribi': 'Middle East',
  'attar collection': 'Middle East', 'hind al oud': 'Middle East',
  'the spirit of dubai': 'Middle East',
  // United States
  'tom ford': 'United States', 'le labo': 'United States',
  'ds and durga': 'United States', 'ds & durga': 'United States', 'd.s. & durga': 'United States',
  'commodity': 'United States', 'mcmc fragrances': 'United States',
  'imaginary authors': 'United States', 'dedcool': 'United States',
  'like this': 'United States', 'heretic parfums': 'United States',
  'phlur': 'United States', 'snif': 'United States',
  'ellis brooklyn': 'United States', 'boy smells': 'United States', 'skylar': 'United States',
  'aerin': 'United States', 'anna sui': 'United States', 'apotheke': 'United States',
  'bond no. 9': 'United States', 'black phoenix alchemy lab': 'United States',
  'brooklyn perfume company': 'United States', 'brooklyn soap company': 'United States',
  'calvin klein': 'United States', 'carolina herrera': 'United States',
  'claire burke': 'United States', 'coach': 'United States',
  'elizabeth taylor': 'United States', 'estée lauder': 'United States', 'estee lauder': 'United States',
  'fulton & roark': 'United States', 'hendley perfumes': 'United States',
  'henry rose': 'United States', 'house of matriarch': 'United States',
  'ineke': 'United States', 'kerosene': 'United States',
  'malin + goetz': 'United States', 'marc jacobs': 'United States',
  'michael kors': 'United States', 'narciso rodriguez': 'United States',
  'oscar de la renta': 'United States', 'ralph lauren': 'United States',
  'régime des fleurs': 'United States', 'regime des fleurs': 'United States',
  'slumberhouse': 'United States', 'sonoma scent studio': 'United States',
  'sweet anthem': 'United States', 'tiffany & co.': 'United States', 'tiffany and co.': 'United States',
  'tocca': 'United States', 'arielle shoshana': 'United States',
  'osswald nyc': 'United States',
  // Scandinavia
  'byredo': 'Scandinavia', 'svensk parfym': 'Scandinavia',
  'vilhelm parfumerie': 'Scandinavia', 'zarkoperfume': 'Scandinavia',
  'andrea maack': 'Scandinavia', 'fischersund': 'Scandinavia',
  // Spain
  'loewe': 'Spain', 'adolfo dominguez': 'Spain', 'carner barcelona': 'Spain',
  'eight & bob': 'Spain',
  // Germany
  'jil sander': 'Germany', 'hugo boss': 'Germany', 'hugo': 'Germany',
  'j.f. schwarzlose berlin': 'Germany', 'joop!': 'Germany', 'joop': 'Germany',
  // Switzerland
  'chopard': 'Switzerland', 'davidoff': 'Switzerland',
  'tauer perfumes': 'Switzerland', 'mizensir': 'Switzerland',
  // Japan
  'issey miyake': 'Japan', 'yohji yamamoto': 'Japan', 'shiseido': 'Japan',
  'shiro': 'Japan', 'parfums kiyoyuki': 'Japan',
  // Australia
  'aesop': 'Australia', 'goldfield & banks': 'Australia',
  'grandiflora': 'Australia', 'naomi goodsir': 'Australia', 'strangers parfumerie': 'Australia',
  // Belgium
  'viktor and rolf': 'Belgium', 'viktor & rolf': 'Belgium', 'une nuit nomade': 'Belgium',
  // Netherlands
  'hiram green': 'Netherlands',
  // Turkey
  'nishane': 'Turkey',
  // South America
  'fueguia 1833': 'South America', 'frassai': 'South America',
  // Canada
  'zoologist': 'Canada',
};

const getRegion = (house: string): string | undefined => {
  const key = house.toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/'/g, '').replace(/'/g, '');
  return HOUSE_REGIONS[key] || HOUSE_REGIONS[key.replace(' and ', ' & ')];
};

// Fuzzy scorer — sequential char match + word-level bonus
// "our ispahan" vs "Oud Ispahan": sequential chars score low, but "ispahan" word match boosts it
const fuzzyScore = (query: string, target: string): number => {
  const q = normalize(query);
  const t = normalize(target);
  if (t.includes(q)) return 1000;

  // Sequential char score
  let qi = 0, score = 0;
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t[i] === q[qi]) { score++; qi++; }
  }

  // Word-level bonus — any query word that exactly matches a target word scores highly
  const qWords = q.split(/\s+/).filter(w => w.length > 2);
  const tWords = t.split(/\s+/);
  for (const qw of qWords) {
    if (tWords.some(tw => tw === qw)) score += 20;        // exact word match
    else if (tWords.some(tw => tw.includes(qw) || qw.includes(tw))) score += 8; // partial word match
  }

  return score;
};

const Discover: React.FC<DiscoverProps> = ({
  userId,
  authToken,
  catalog,
  catalogLoading,
  ingredients,
  wishlistIds,
  journalIds,
  journalEntries,
  topFamilies,
  scentSignals,
  noteSentimentMap,
  onWishlistToggle,
  onJournalSaved,
  onCatalogUpdated,
  onContribution,
}) => {
  const [discoverSubTab, setDiscoverSubTab] = useState<'catalog' | 'notes'>('catalog');
  const [query, setQuery] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [journalFragrance, setJournalFragrance] = useState<Fragrance | null>(null);
  const [journalPresetRating, setJournalPresetRating] = useState<string | null>(null);
  const [journalViewFragrance, setJournalViewFragrance] = useState<Fragrance | null>(null);
  const [lookupMessage, setLookupMessage] = useState('');
  const [contributionToast, setContributionToast] = useState<{ name: string; house: string; earnedRefresh: boolean } | null>(null);
  const [houseFilter, setHouseFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [sortBy, setSortBy] = useState<'az' | 'love' | 'like' | 'wishlist' | 'owned' | 'polarizing' | 'match'>('match');
  const [ingredientPopup, setIngredientPopup] = useState<Ingredient | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<ScentCategory | ''>('');
  const [bottleLightbox, setBottleLightbox] = useState<{ name: string; house: string; year?: number | null; imageUrl: string; sourceUrl?: string } | null>(null);
  const [variantSuggestions, setVariantSuggestions] = useState<Fragrance[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Build direct rating map (fragranceId → rating) from journal
  const directRatings = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of journalEntries) {
      if (entry.rating) map.set(entry.fragranceId, entry.rating);
    }
    return map;
  }, [journalEntries]);

  // Build note weight map — loved notes boost, disliked notes penalise
  const likedNotes = useMemo(() => buildLikedNotes(journalEntries, catalog, noteSentimentMap || undefined), [journalEntries, catalog, noteSentimentMap]);

  // Pre-compute compat score for every fragrance
  const compatScoreMap = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const f of catalog) {
      const base = computeCompatScore(f, topFamilies, likedNotes);
      if (base === null) { map.set(f.fragranceId, null); continue; }
      const modifier = scentSignals ? computeSignalModifier(f, scentSignals) : 0;
      let score = Math.round(Math.max(0, Math.min(100, base + modifier)));
      // Direct ratings override inferred signals — explicit trumps everything
      const rating = directRatings.get(f.fragranceId);
      if (rating === 'love') score = Math.max(score, 88);
      else if (rating === 'like') score = Math.max(score, 70);
      else if (rating === 'dislike') score = Math.min(score, 18);
      map.set(f.fragranceId, score);
    }
    return map;
  }, [catalog, topFamilies, likedNotes, scentSignals, directRatings]);

  const compatBreakdownMap = useMemo(() => {
    const map = new Map<string, CompatResult | null>();
    for (const f of catalog) {
      map.set(f.fragranceId, computeCompatBreakdown(f, topFamilies, likedNotes));
    }
    return map;
  }, [catalog, topFamilies, likedNotes]);

  // Pre-compute scent category for every fragrance
  const fragranceCategoryMap = useMemo(() => {
    const map = new Map<string, { category: ScentCategory; cx: number; cy: number } | null>();
    for (const f of catalog) {
      map.set(f.fragranceId, computeFragranceCategory(f, ingredients));
    }
    return map;
  }, [catalog, ingredients]);

  // Derive house and region lists from catalog
  const allHouses = Array.from(new Set(catalog.map(f => f.house).filter(Boolean))).sort();
  const allRegions = Array.from(new Set(
    catalog.map(f => getRegion(f.house) || 'Other')
  )).sort();

  // Houses available given current regionFilter
  const houses = regionFilter
    ? allHouses.filter(h => (getRegion(h) || 'Other') === regionFilter)
    : allHouses;

  // Regions available given current houseFilter
  const regions = houseFilter
    ? [getRegion(houseFilter) || 'Other']
    : allRegions;

  // Filter + sort catalog client-side
  const queries = query.trim() ? expandQuery(query) : [];
  const displayList = catalog.filter(f => {
    if (f.house === 'Unknown') return false;
    const houseMatch = !houseFilter || f.house === houseFilter;
    const regionMatch = !regionFilter || (getRegion(f.house) || 'Other') === regionFilter;
    const searchMatch = !query.trim() || matchesFragrance(f, queries);
    const catMatch = !categoryFilter || fragranceCategoryMap.get(f.fragranceId)?.category === categoryFilter;
    return houseMatch && regionMatch && searchMatch && catMatch;
  }).sort((a, b) => {
    if (sortBy === 'love') return (b.loveCt || 0) - (a.loveCt || 0);
    if (sortBy === 'like') return (b.likeCt || 0) - (a.likeCt || 0);
    if (sortBy === 'wishlist') return (b.wishlistCt || 0) - (a.wishlistCt || 0);
    if (sortBy === 'owned') return (b.ownedCt || 0) - (a.ownedCt || 0);
    if (sortBy === 'polarizing') return (b.neutralCt || 0) - (a.neutralCt || 0);
    if (sortBy === 'match') {
      const sa = compatScoreMap.get(a.fragranceId) ?? -1;
      const sb = compatScoreMap.get(b.fragranceId) ?? -1;
      return sb - sa;
    }
    return a.name.localeCompare(b.name);
  });

  const hasSearched = query.trim().length > 0;
  const noResults = hasSearched && displayList.length === 0;

  // "Did you mean?" — top 3 fuzzy matches when no results
  const suggestions = noResults
    ? catalog
        .filter(f => f.house !== 'Unknown')
        .map(f => ({ f, score: Math.max(fuzzyScore(query, f.name), fuzzyScore(query, f.house)) }))
        .filter(x => x.score > 2)
        .sort((a, b) => b.score - a.score)
        .slice(0, 3)
        .map(x => x.f)
    : [];

  // Find catalog variants of a found fragrance (same house, same base name, different concentration)
  const findVariants = (foundName: string, foundHouse: string, foundId: string): Fragrance[] => {
    // Strip known concentration suffixes to get the base name
    const suffixPattern = /\s+(EDT|EDP|Parfum|Intense|Elixir|Extrait|Absolue|Absolu|Forte|Concentrée|Aqua|Extrême|Le Parfum|Parfum d'Eau|Parfum d'Eau|Eau Première|Eau de Parfum|Eau de Toilette|Suprême|Intensément|Intensely|Legend|Flame|Aurora|Lumineuse|Initial)\s*$/i;
    const baseName = normalize(foundName.replace(suffixPattern, '').trim());
    return catalog.filter(f => {
      if (f.house !== foundHouse || f.fragranceId === foundId) return false;
      const fBase = normalize(f.name.replace(suffixPattern, '').trim());
      // Match if the bases are the same (variant of same fragrance)
      return fBase === baseName && f.name !== foundName;
    });
  };

  const handleLookup = async () => {
    if (!query.trim()) return;
    setLookingUp(true);
    setLookupMessage('');
    setVariantSuggestions([]);
    try {
      const res = await axios.post(`${API_URL}/fragrances`, { name: query, userId }, {
        headers: { Authorization: `Bearer ${authToken}` },
        timeout: 28000,
      });
      if (res.data.found) {
        if (res.data.isNewContribution) {
          setContributionToast({ name: res.data.name, house: res.data.house, earnedRefresh: res.data.earnedRefresh });
          setTimeout(() => setContributionToast(null), 5000);
          if (onContribution) onContribution();
        } else {
          setLookupMessage(`✓ Found ${res.data.name} by ${res.data.house}`);
        }
        onCatalogUpdated();
        setQuery(res.data.name);
        // Check catalog for other concentrations of this fragrance (already seeded)
        const variants = findVariants(res.data.name, res.data.house, res.data.fragranceId);
        setVariantSuggestions(variants);
        // Silently seed any variants Haiku knows about that aren't in the catalog yet
        if (res.data.isNewContribution && res.data.variants?.length) {
          const catalogNames = new Set(catalog.map((f: any) => f.name.toLowerCase()));
          const toSeed = (res.data.variants as string[]).filter(v => !catalogNames.has(v.toLowerCase()));
          toSeed.forEach(variantName => {
            axios.post(`${API_URL}/fragrances`, { name: variantName }, {
              headers: { Authorization: `Bearer ${authToken}` },
              timeout: 25000,
            }).catch(() => {}); // fire-and-forget — failures are silent
          });
        }
      } else {
        setLookupMessage("Our Concierge is stumped on this one. Tap the ? in the bottom right and let us know — we'll add it for you and make sure it counts toward your total.");
      }
    } catch (e: any) {
      if (e?.code === 'ECONNABORTED') {
        setLookupMessage("Our Concierge is hard at work but needs a little help. Tap the ? in the bottom right and let us know — we'll add it for you and make sure it counts toward your total.");
      } else {
        setLookupMessage("Our Concierge is stumped on this one. Tap the ? in the bottom right and let us know — we'll add it for you and make sure it counts toward your total.");
      }
    }
    setLookingUp(false);
  };

  const handlePhotoCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLookingUp(true);
    setLookupMessage('Scanning bottle...');
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = (reader.result as string).split(',')[1];
      try {
        const res = await axios.post(`${API_URL}/scan`, { image: base64 });
        if (res.data.fragrance) {
          setQuery(res.data.fragrance.name);
          setLookupMessage('');
        } else {
          setLookupMessage('Could not identify the bottle. Try searching by name.');
        }
      } catch (e) {
        setLookupMessage('Scan failed. Try searching by name.');
      }
      setLookingUp(false);
    };
    reader.readAsDataURL(file);
  };

  const lookupIngredient = (noteName: string): Ingredient | undefined => {
    // Build a list of candidate IDs/names to try, from most to least specific
    const candidates: string[] = [];
    const base = noteName.trim();

    // 1. Exact
    candidates.push(base.toLowerCase().replace(/\s+/g, '-'));
    // 2. Strip parenthetical: "Ambrette (Musk Mallow)" → "ambrette"
    const noParens = base.replace(/\s*\(.*?\)/g, '').trim();
    if (noParens !== base) candidates.push(noParens.toLowerCase().replace(/\s+/g, '-'));
    // 3. Remove spaces entirely: "Black Currant" → "blackcurrant"
    candidates.push(base.toLowerCase().replace(/\s+/g, ''));
    // 4. Singular (strip trailing s/ies→y/ies→y)
    const id = base.toLowerCase().replace(/\s+/g, '-');
    if (id.endsWith('ies')) candidates.push(id.slice(0, -3) + 'y');
    else if (id.endsWith('s')) candidates.push(id.slice(0, -1));
    // 5. Known specific aliases
    const ALIASES: Record<string, string> = {
      // Existing
      'tonka': 'tonka-bean',
      'petit-grain': 'petitgrain',
      'agar-wood': 'agarwood',
      'sambac-jasmine': 'jasmine-sambac',
      'jasmine-absolute': 'jasmine',
      'vetivert': 'vetiver',
      'vetyver': 'vetiver',
      'vetiveryl-acetate': 'vetiver',
      'ammber': 'amber',
      'ambregris': 'ambergris',
      'red-rose': 'rose',
      'damascena-rose': 'rose',
      'bulgarian-rose': 'rose',
      'pink-grapefruit': 'grapefruit',
      'atlas-cedarwood': 'cedar',
      'cedar-leaf': 'cedar',
      'cedrus': 'cedar',
      'oakwood': 'oak',
      'seawater': 'sea-notes',
      'sea-water': 'sea-notes',
      'marine-notes': 'sea-notes',
      'aquatic': 'sea-notes',
      'spices': 'spice',
      'spice': 'spice',
      'herbs': 'herb',
      'herbal-notes': 'herb',
      'aromatic-herbs': 'herb',
      'italian-lemon': 'lemon',
      'lemon-leaves': 'lemon',
      'lemon-blossom': 'lemon',
      'fig-leaves': 'fig',
      'green-rose': 'rose',
      'white-jasmine': 'jasmine',
      'white-pepper': 'pepper',
      'black-pepper': 'pepper',
      'white-musks': 'musk',
      'musks': 'musk',
      'solar-musk': 'musk',
      'chantilly-musk': 'musk',
      'blackberry-musk': 'musk',
      'dark-amber': 'amber',
      'orange-flower': 'neroli',
      'vanille': 'vanilla',
      'citruses': 'citrus',
      'bergamot-oil': 'bergamot',
      'earthy': 'vetiver',
      'earthy-notes': 'vetiver',
      'forest-floor-notes': 'vetiver',
      'wood': 'wood',
      'woods': 'wood',
      'woody': 'wood',
      'dry-woods': 'wood',
      'white-woods': 'wood',
      'grass': 'galbanum',
      'oolong-tea': 'tea',
      'orange-blossom': 'neroli',
    };
    const normalized_id = base.toLowerCase().replace(/\s+/g, '-');
    if (ALIASES[normalized_id]) candidates.push(ALIASES[normalized_id]);

    // Try all candidate IDs against the ingredients list
    for (const cid of candidates) {
      const found = ingredients.find(ing =>
        ing.ingredientId === cid || normalize(ing.name) === normalize(cid.replace(/-/g, ' '))
      );
      if (found) return found;
    }
    // Final: normalize name match
    return ingredients.find(ing => normalize(ing.name) === normalize(base));
  };

  if (discoverSubTab === 'notes') {
    return (
      <>
        <div className="border-b border-cream-dark bg-cream sticky top-[61px] z-20">
          <div className="max-w-2xl mx-auto px-6 flex gap-6">
            <button onClick={() => setDiscoverSubTab('catalog')} className="text-xs tracking-widest uppercase py-3 text-stone-light hover:text-stone transition-colors">Fragrances</button>
            <button className="text-xs tracking-widest uppercase py-3 text-charcoal border-b-2 border-charcoal">Notes</button>
          </div>
        </div>
        <Glossary ingredients={ingredients} journalEntries={journalEntries} catalog={catalog} />
      </>
    );
  }

  return (
    <>
      {/* Contribution toast */}
      {contributionToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-charcoal text-cream px-5 py-3 rounded-xl shadow-lg text-sm text-center max-w-xs">
          <p className="font-serif text-base mb-0.5">✦ Added to the Scentd catalog</p>
          <p className="text-stone-light text-xs">{contributionToast.name} by {contributionToast.house} is now searchable by everyone — thanks for contributing!</p>
          <p className="text-stone-light text-xs mt-1">Ingredient profiles will be ready in a few minutes. Enjoy exploring in the meantime.</p>
          {contributionToast.earnedRefresh && (
            <p className="text-cream text-xs mt-1.5 font-medium">✦ You earned a Concierge refresh</p>
          )}
        </div>
      )}
      <div className="min-h-screen bg-cream pb-24 md:pb-10">
        {/* Sub-tabs: Fragrances | Notes */}
        <div className="border-b border-cream-dark bg-cream sticky top-[61px] z-20">
          <div className="max-w-2xl mx-auto px-6 flex gap-6">
            <button className="text-xs tracking-widest uppercase py-3 text-charcoal border-b-2 border-charcoal">Fragrances</button>
            <button onClick={() => setDiscoverSubTab('notes')} className="text-xs tracking-widest uppercase py-3 text-stone-light hover:text-stone transition-colors">Notes</button>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-6 py-10">

          {/* Hero */}
          {!query.trim() && !houseFilter && !regionFilter && (
            <div className="text-center mb-10">
              <h1 className="font-serif text-5xl text-charcoal tracking-wide mb-3">Scentd</h1>
              <p className="text-stone text-sm leading-relaxed mb-1">Your personal fragrance journal.</p>
              <p className="text-stone-light text-xs leading-relaxed">Discover your scent identity, track what you wear, and explore the world of fine fragrance.</p>
            </div>
          )}

          {/* Search bar */}
          <div className="mb-3">
            <div className="relative">
              <input
                type="text"
                value={query}
                onChange={e => { setQuery((e.target as HTMLInputElement).value); setLookupMessage(''); setVariantSuggestions([]); }}
                onKeyDown={e => e.key === 'Enter' && noResults && !suggestions.length && handleLookup()}
                placeholder="Search by name, house, or note..."
                className="w-full bg-white border border-cream-dark rounded-xl px-4 py-4 pr-20 text-charcoal placeholder-stone-light focus:outline-none focus:border-stone transition-colors"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                {query.trim() && (
                  <button
                    onClick={() => { setQuery(''); setHouseFilter(''); setRegionFilter(''); setLookupMessage(''); setVariantSuggestions([]); }}
                    className="text-stone-light hover:text-charcoal transition-colors"
                    title="Clear"
                  >
                    <IconX size={16} />
                  </button>
                )}
                {/* CAMERA SCAN — temporarily hidden until bottle scan is ready
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-stone-light hover:text-charcoal transition-colors"
                  title="Scan bottle"
                >
                  <IconCamera size={18} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoCapture}
                  className="hidden"
                />
                */}
              </div>
            </div>
          </div>

          {/* House + Region filters + Sort */}
          <div className="flex gap-2 flex-wrap mb-4">
            <select
              value={regionFilter}
              onChange={e => {
                const r = (e.target as HTMLSelectElement).value;
                setRegionFilter(r);
                // Clear house if it doesn't belong to the new region
                if (r && houseFilter && (getRegion(houseFilter) || 'Other') !== r) {
                  setHouseFilter('');
                }
              }}
              className="bg-white border border-cream-dark text-stone text-xs tracking-widest uppercase px-3 py-2 rounded-xl focus:outline-none focus:border-stone transition-colors min-w-[120px]"
            >
              <option value="">All regions</option>
              {regions.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <select
              value={houseFilter}
              onChange={e => {
                const h = (e.target as HTMLSelectElement).value;
                setHouseFilter(h);
                // Auto-set region to match selected house
                if (h) setRegionFilter(getRegion(h) || 'Other');
                else if (!h) setRegionFilter('');
              }}
              className="bg-white border border-cream-dark text-stone text-xs tracking-widest uppercase px-3 py-2 rounded-xl focus:outline-none focus:border-stone transition-colors min-w-[130px]"
            >
              <option value="">All houses</option>
              {houses.map(h => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
            <select
              value={sortBy}
              onChange={e => setSortBy((e.target as HTMLSelectElement).value as any)}
              className="bg-white border border-cream-dark text-stone text-xs tracking-widest uppercase px-3 py-2 rounded-xl focus:outline-none focus:border-stone transition-colors min-w-[120px]"
            >
              <option value="az">A – Z</option>
              {topFamilies.length > 0 && <option value="match">My Match %</option>}
              <option value="love">★ All-Time Faves</option>
              <option value="like">Most Liked</option>
              <option value="wishlist">+ Most Wishlisted</option>
              <option value="owned">% Most Owned</option>
              <option value="polarizing">~ Most Polarizing</option>
            </select>
          </div>

          {/* Category filter chips */}
          <div className="flex gap-2 flex-wrap mb-4">
            {(Object.keys(SCENT_CATEGORIES) as ScentCategory[]).map(cat => {
              const { color, light, label } = SCENT_CATEGORIES[cat];
              const active = categoryFilter === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(active ? '' : cat)}
                  style={active ? { background: color, borderColor: color, color: '#fff' } : { background: light, borderColor: color, color: label }}
                  className="text-xs tracking-widest uppercase px-3 py-1.5 rounded-full border transition-colors font-sans"
                >
                  {cat}
                </button>
              );
            })}
          </div>

          {/* No results — show lookup prompt */}
          {noResults && (
            <div className="bg-cream-dark rounded-xl p-4 mb-6">
              <p className="text-stone text-sm mb-3">"{query}" isn't in our catalog yet.</p>
              <button
                onClick={handleLookup}
                disabled={lookingUp}
                className="bg-charcoal text-cream px-6 py-2 text-xs tracking-widest uppercase hover:bg-charcoal-light transition-colors disabled:opacity-50 mb-3"
              >
                {lookingUp ? 'Your Scentd Concierge is on it...' : 'Find & add to catalog ✨'}
              </button>
              {lookingUp && (
                <p className="text-stone-light text-xs mb-2 animate-pulse">This can take 10–20 seconds — hang tight.</p>
              )}
              {lookupMessage && <p className="text-stone text-xs mt-1 mb-3">{lookupMessage}</p>}
              {variantSuggestions.length > 0 && (
                <div className="mt-2 mb-3">
                  <p className="text-stone-light text-xs tracking-widest uppercase mb-2">Also available</p>
                  <div className="flex flex-wrap gap-2">
                    {variantSuggestions.map(v => (
                      <button
                        key={v.fragranceId}
                        onClick={() => { setQuery(v.name); setVariantSuggestions([]); setLookupMessage(''); }}
                        className="text-xs px-3 py-1.5 rounded-full border border-cream-dark bg-white text-stone hover:border-stone hover:text-charcoal transition-colors"
                      >
                        {v.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {suggestions.length > 0 && (
                <>
                  <p className="text-stone-light text-xs tracking-widest uppercase mb-2">Or did you mean?</p>
                  <div className="space-y-2">
                    {suggestions.map(s => (
                      <button
                        key={s.fragranceId}
                        onClick={() => { setQuery(s.name); setLookupMessage(''); }}
                        className="block text-left text-charcoal hover:text-stone transition-colors"
                      >
                        <span className="font-serif">{s.name}</span>
                        <span className="text-stone-light text-sm ml-2">{s.house}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Partial results — show "not seeing it?" add prompt */}
          {!noResults && query.trim().length > 1 && (
            <div className="bg-cream-dark rounded-xl p-4 mb-6">
              <p className="text-stone text-sm mb-3">Not seeing the right fragrance?</p>
              <button
                onClick={handleLookup}
                disabled={lookingUp}
                className="bg-charcoal text-cream px-6 py-2 text-xs tracking-widest uppercase hover:bg-charcoal-light transition-colors disabled:opacity-50 mb-3"
              >
                {lookingUp ? 'Your Scentd Concierge is on it...' : 'Find & add to catalog ✨'}
              </button>
              {lookingUp && (
                <p className="text-stone-light text-xs mb-2 animate-pulse">This can take 10–20 seconds — hang tight.</p>
              )}
              {lookupMessage && <p className="text-stone text-xs mt-1">{lookupMessage}</p>}
            </div>
          )}

          {lookingUp && !noResults && (
            <p className="text-stone-light text-sm mb-4">Your Scentd Concierge is on it...</p>
          )}

          {/* Count / loading */}
          {catalogLoading ? (
            <>
              <p className="text-stone-light text-xs tracking-widest uppercase mb-4 animate-pulse">Loading catalog...</p>
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white border border-cream-dark rounded-xl p-5 animate-pulse">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="h-4 bg-cream-dark rounded w-2/5 mb-2" />
                        <div className="h-3 bg-cream-dark rounded w-1/4" />
                      </div>
                      <div className="flex gap-3 ml-3">
                        <div className="h-4 w-4 bg-cream-dark rounded-full" />
                        <div className="h-4 w-4 bg-cream-dark rounded-full" />
                        <div className="h-4 w-4 bg-cream-dark rounded-full" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-stone-light text-xs tracking-widest uppercase mb-4">
              {displayList.length} {displayList.length === 1 ? 'fragrance' : 'fragrances'}
              {!query.trim() && !houseFilter && ` in catalog`}
            </p>
          )}

          {/* Fragrance list */}
          {!catalogLoading && <div className="space-y-2">
            {displayList.map(f => {
              const isWishlisted = wishlistIds.has(f.fragranceId);
              const isJournaled = journalIds.has(f.fragranceId);
              const isExpanded = expanded === f.fragranceId;
              const allNotes = [...(f.topNotes || []), ...(f.middleNotes || []), ...(f.baseNotes || []), ...(f.notes || [])];
              const uniqueNotes = Array.from(new Set(allNotes));
              const catResult = fragranceCategoryMap.get(f.fragranceId);
              const cat = catResult?.category;
              const catStyle = cat ? SCENT_CATEGORIES[cat] : null;
              const cx = catResult?.cx ?? 0;
              const cy = catResult?.cy ?? 0;
              const borderColor = catStyle?.color || '#E8E2D8';
              const lightBg = catStyle?.light || '#F5F0E8';
              const labelColor = catStyle?.label || '#9E9688';
              const SIZE = 136, C = SIZE / 2, SCALE = 52; // mini-map (collapsed)
              const toSvgX = (v: number) => C + v * SCALE;
              const toSvgY = (v: number) => C - v * SCALE;
              const MS = 220, MC = MS / 2, MSCALE = 88; // full scent map
              const toMapX = (v: number) => MC + v * MSCALE;
              const toMapY = (v: number) => MC - v * MSCALE;

              const compatScore = compatScoreMap.get(f.fragranceId) ?? null;

              return (
                <div key={f.fragranceId} className="bg-white rounded-xl overflow-hidden" style={{ border: `1.5px solid ${borderColor}` }}>

                  {/* ── COLLAPSED — click anywhere except action buttons to expand ── */}
                  <div
                    className="p-4 flex items-center gap-2 cursor-pointer"
                    onClick={() => setExpanded(isExpanded ? null : f.fragranceId)}
                  >
                    {/* Bottle circle with arc ring */}
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="relative" style={{ width: 46, height: 46 }}>
                        <svg width="46" height="46" viewBox="0 0 46 46" style={{ position: 'absolute', inset: 0 }}>
                          <circle cx="23" cy="23" r="19" fill="none" stroke="#EDE8DF" strokeWidth="4" pathLength="100"/>
                          <circle cx="23" cy="23" r="19" fill="none" stroke={borderColor} strokeWidth="4"
                            pathLength="100"
                            strokeDasharray={compatScore !== null ? `${compatScore} ${100 - compatScore}` : '0 100'}
                            strokeDashoffset="25" strokeLinecap="round"/>
                          <circle cx="23" cy="23" r="15" fill={borderColor + '18'}/>
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <svg width="14" height="22" viewBox="0 0 22 32" fill="none">
                            <rect x="7" y="0" width="8" height="5" rx="2" fill={borderColor}/>
                            <path d="M4 8 Q4 6 7 6 L15 6 Q18 6 18 8 L20 28 Q20 31 17 31 L5 31 Q2 31 2 28 Z" fill={borderColor} opacity="0.35"/>
                            <path d="M4 8 Q4 6 7 6 L15 6 Q18 6 18 8 L20 28 Q20 31 17 31 L5 31 Q2 31 2 28 Z" fill="none" stroke={borderColor} strokeWidth="1.2"/>
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Name + house + badge */}
                    <div className="text-left min-w-0 flex-1">
                      <p className="font-serif text-base text-charcoal leading-snug">{f.name}</p>
                      <p className="text-stone-light text-xs mt-0.5">{f.house}{f.year ? ` · ${f.year}` : ''}</p>
                      <div className="flex items-center gap-1.5 flex-wrap mt-1">
                        {compatScore !== null && (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: borderColor + '22', color: labelColor, border: `1px solid ${borderColor}66` }}>
                            {compatScore}% match
                          </span>
                        )}
                        {cat && (
                          <span className="text-xs tracking-widest uppercase px-2 py-0.5 rounded-full border"
                            style={{ color: labelColor, background: lightBg, borderColor }}>
                            {cat}
                          </span>
                        )}
                        {isJournaled && (
                          <span
                            className="text-xs bg-charcoal text-cream px-2 py-0.5 rounded-md tracking-widest uppercase cursor-pointer hover:opacity-75 transition-opacity"
                            onClick={e => { e.stopPropagation(); setJournalViewFragrance(f); }}
                          >Journaled ↗</span>
                        )}
                      </div>
                    </div>

                    {/* Actions — stopPropagation so clicks don't toggle expand */}
                    <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => { setJournalPresetRating('like'); setJournalFragrance(f); }}
                        className="w-7 h-7 rounded-full border border-cream-dark bg-white flex items-center justify-center hover:border-stone transition-colors"
                        title={isJournaled ? 'Edit journal entry' : 'Like & log it'}
                      >
                        <IconHeart size={13} filled={isJournaled} className={isJournaled ? 'text-charcoal' : 'text-stone-light'} />
                      </button>
                      <button
                        onClick={() => onWishlistToggle(f.fragranceId, f.name, f.house)}
                        className={`w-7 h-7 rounded-full border bg-white flex items-center justify-center transition-colors ${isWishlisted ? 'border-charcoal hover:border-stone' : 'border-cream-dark hover:border-stone'}`}
                        title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                      >
                        <IconPlus size={13} strokeWidth={isWishlisted ? 2.5 : 1.5} className={isWishlisted ? 'text-charcoal' : 'text-stone-light'} />
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); setExpanded(isExpanded ? null : f.fragranceId); }}
                        className="w-7 h-7 rounded-full border border-cream-dark bg-white flex items-center justify-center hover:border-stone transition-colors"
                        title={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        <IconChevron size={11} direction={isExpanded ? 'up' : 'down'} className="text-stone-light" />
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-cream-dark px-4 pt-4 pb-5 space-y-4">

                      {/* Description */}
                      {f.description && (
                        <p className="text-stone text-sm leading-relaxed">{f.description}</p>
                      )}

                      {/* Compat score breakdown */}
                      {(() => {
                        const bd = compatBreakdownMap.get(f.fragranceId);
                        if (!bd || topFamilies.length === 0) return null;
                        return (
                          <div className="bg-cream rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="relative" style={{ width: 32, height: 32 }}>
                                <svg width="32" height="32" viewBox="0 0 40 40">
                                  <circle cx="20" cy="20" r="16" fill="none" stroke="#EDE8DF" strokeWidth="3" pathLength="100"/>
                                  <circle cx="20" cy="20" r="16" fill="none" stroke={catStyle?.color || '#C4A882'} strokeWidth="3"
                                    pathLength="100" strokeDasharray={`${compatScoreMap.get(f.fragranceId) ?? bd.score} ${100 - (compatScoreMap.get(f.fragranceId) ?? bd.score)}`}
                                    strokeDashoffset="25" strokeLinecap="round"/>
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center" style={{ fontSize: 7, fontWeight: 700, color: '#2A2520' }}>{compatScoreMap.get(f.fragranceId) ?? bd.score}%</div>
                              </div>
                              <p className="text-xs tracking-widest uppercase text-stone-light">Why this match</p>
                            </div>
                            <div className="space-y-2">
                              {/* Direct rating — shown first, explains score floor/ceiling */}
                              {directRatings.get(f.fragranceId) === 'love' && (
                                <div className="flex gap-2 items-start">
                                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: '#8CA87A' }}/>
                                  <p className="text-xs text-stone leading-relaxed">
                                    <span className="text-charcoal font-medium">You've marked this as an all-time fave</span> — score reflects your rating
                                  </p>
                                </div>
                              )}
                              {directRatings.get(f.fragranceId) === 'like' && (
                                <div className="flex gap-2 items-start">
                                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: '#8CA87A' }}/>
                                  <p className="text-xs text-stone leading-relaxed">
                                    <span className="text-charcoal font-medium">You've liked this</span> — score reflects your rating
                                  </p>
                                </div>
                              )}
                              {directRatings.get(f.fragranceId) === 'dislike' && (
                                <div className="flex gap-2 items-start">
                                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: '#C4697A' }}/>
                                  <p className="text-xs text-stone leading-relaxed">
                                    <span className="text-charcoal font-medium">You've marked this as not for you</span> — score reflects your rating
                                  </p>
                                </div>
                              )}
                              {bd.familyMatches.map(({ family, notes }) => {
                                const rank = topFamilies.findIndex(f => f.category === family);
                                const pct = topFamilies[rank]?.pct;
                                return (
                                  <div key={family} className="flex gap-2 items-start">
                                    <div className="w-1.5 h-1.5 rounded-full bg-charcoal flex-shrink-0 mt-1.5"/>
                                    <p className="text-xs text-stone leading-relaxed">
                                      <span className="text-charcoal font-medium">{notes.join(', ')}</span>
                                      {' '}→ {family} ({pct ? `${pct}% of your taste` : `your #${rank + 1} family`})
                                    </p>
                                  </div>
                                );
                              })}
                              {bd.likedNoteMatches.length > 0 && (
                                <div className="flex gap-2 items-start">
                                  <div className="w-1.5 h-1.5 rounded-full bg-charcoal flex-shrink-0 mt-1.5"/>
                                  <p className="text-xs text-stone leading-relaxed">
                                    <span className="text-charcoal font-medium">{bd.likedNoteMatches.slice(0, 4).join(', ')}{bd.likedNoteMatches.length > 4 ? ` +${bd.likedNoteMatches.length - 4}` : ''}</span>
                                    {' '}appear in fragrances you've liked
                                  </p>
                                </div>
                              )}
                              {bd.unknownNotes.length > 0 && (
                                <div className="flex gap-2 items-start">
                                  <div className="w-1.5 h-1.5 rounded-full bg-stone-light flex-shrink-0 mt-1.5"/>
                                  <p className="text-xs text-stone-light leading-relaxed">
                                    {bd.unknownNotes.join(', ')} — not yet in your profile
                                  </p>
                                </div>
                              )}
                              {/* Signal modifiers from Scent Identity */}
                              {scentSignals && (() => {
                                const desc = (f.description || '').toLowerCase();
                                const allN = Array.from(new Set([...(f.notes||[]),...(f.topNotes||[]),...(f.middleNotes||[]),...(f.baseNotes||[])]));
                                const fragFams = allN.map(n => guessNoteFamily(n)).filter(Boolean) as string[];
                                const hits = (scentSignals.preferredDescriptors || []).filter(d => desc.includes(d.toLowerCase()));
                                const misses = (scentSignals.avoidedDescriptors || []).filter(d => desc.includes(d.toLowerCase()));
                                const avoidedFamHits = (scentSignals.avoidedFamilies || []).filter(fam => fragFams.some(f => f === fam));
                                if (hits.length === 0 && misses.length === 0 && avoidedFamHits.length === 0) return null;
                                return (
                                  <>
                                    {hits.length > 0 && (
                                      <div className="flex gap-2 items-start">
                                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: '#8CA87A' }}/>
                                        <p className="text-xs text-stone leading-relaxed">
                                          <span className="text-charcoal font-medium">{hits.join(', ')}</span> — matches your Scent Identity profile
                                        </p>
                                      </div>
                                    )}
                                    {(misses.length > 0 || avoidedFamHits.length > 0) && (
                                      <div className="flex gap-2 items-start">
                                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: '#C4697A' }}/>
                                        <p className="text-xs text-stone leading-relaxed">
                                          <span className="text-charcoal font-medium">{[...misses, ...avoidedFamHits].join(', ')}</span> — your profile suggests you tend to avoid this
                                        </p>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Scent map — full width, large, styled like archetype map */}
                      {catResult && (
                        <div>
                          <p className="text-xs tracking-widest uppercase text-stone-light text-center mb-2">Scent map</p>
                          <svg width="100%" viewBox={`0 0 ${MS} ${MS}`} className="w-full max-w-xs mx-auto block">
                            <defs>
                              <radialGradient id={`blob-${f.fragranceId}`} cx={toMapX(cx)} cy={toMapY(cy)} r={MS * 0.38} gradientUnits="userSpaceOnUse">
                                <stop offset="0%"   stopColor={borderColor} stopOpacity="0.55"/>
                                <stop offset="50%"  stopColor={borderColor} stopOpacity="0.2"/>
                                <stop offset="100%" stopColor={borderColor} stopOpacity="0"/>
                              </radialGradient>
                            </defs>
                            {/* Axis lines — match archetype map style */}
                            <line x1={MC} y1="8" x2={MC} y2={MS-8} stroke="#D4CEBF" strokeWidth="0.5"/>
                            <line x1="8" y1={MC} x2={MS-8} y2={MC} stroke="#D4CEBF" strokeWidth="0.5"/>
                            {/* Axis endpoint labels */}
                            <text x={MC} y="13" textAnchor="middle" fontSize="8" fill={labelColor} fontFamily="sans-serif" letterSpacing="0.1em" opacity="0.7">BRIGHT</text>
                            <text x={MC} y={MS-5} textAnchor="middle" fontSize="8" fill={labelColor} fontFamily="sans-serif" letterSpacing="0.1em" opacity="0.7">DEEP</text>
                            <text x="8" y={MC-3} textAnchor="start" fontSize="8" fill={labelColor} fontFamily="sans-serif" letterSpacing="0.1em" opacity="0.7">FRESH</text>
                            <text x={MS-8} y={MC-3} textAnchor="end" fontSize="8" fill={labelColor} fontFamily="sans-serif" letterSpacing="0.1em" opacity="0.7">RICH</text>
                            {/* Quadrant labels — each in its own category color */}
                            <text x="10" y="30" fontSize="7" fill="#4A8FA0" fontFamily="sans-serif" letterSpacing="0.08em" opacity="0.75">AIR</text>
                            <text x={MS-10} y="30" textAnchor="end" fontSize="7" fill="#9A5F68" fontFamily="sans-serif" letterSpacing="0.08em" opacity="0.75">BLOOM</text>
                            <text x="10" y={MS-16} fontSize="7" fill="#4A6E3A" fontFamily="sans-serif" letterSpacing="0.08em" opacity="0.75">TERRAIN</text>
                            <text x={MS-10} y={MS-16} textAnchor="end" fontSize="7" fill="#2E2A34" fontFamily="sans-serif" letterSpacing="0.08em" opacity="0.75">NOIR</text>
                            {/* Gradient blob */}
                            <rect x="0" y="0" width={MS} height={MS} fill={`url(#blob-${f.fragranceId})`}/>
                            {/* Dot — styled like archetype map pin */}
                            <circle cx={toMapX(cx)} cy={toMapY(cy)} r="7" fill="none" stroke={borderColor} strokeWidth="1.5" opacity="0.9"/>
                            <circle cx={toMapX(cx)} cy={toMapY(cy)} r="3.5" fill={borderColor}/>
                          </svg>
                        </div>
                      )}

                      {/* Notes — colored by tier */}
                      {(f.topNotes?.length || f.middleNotes?.length || f.baseNotes?.length || uniqueNotes.length > 0) && (
                        <div className="space-y-2.5">
                          <p className="text-xs tracking-widest uppercase text-stone-light">Notes</p>
                          {f.topNotes && f.topNotes.length > 0 && (
                            <div>
                              <p className="text-xs tracking-widest uppercase mb-1.5" style={{ color: '#9A7238' }}>Top</p>
                              <div className="flex flex-wrap gap-1.5">
                                {f.topNotes.map((n, i) => {
                                  const ing = lookupIngredient(n);
                                  return (
                                    <button key={i} onClick={() => ing && setIngredientPopup(ing)}
                                      className={`text-xs px-2.5 py-1 rounded-full border ${ing ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                                      style={{ background: '#FBF0DC', color: '#7A5C28', borderColor: '#EDD89A' }}>
                                      {n}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {f.middleNotes && f.middleNotes.length > 0 && (
                            <div>
                              <p className="text-xs tracking-widest uppercase mb-1.5" style={{ color: '#9A4F5E' }}>Heart</p>
                              <div className="flex flex-wrap gap-1.5">
                                {f.middleNotes.map((n, i) => {
                                  const ing = lookupIngredient(n);
                                  return (
                                    <button key={i} onClick={() => ing && setIngredientPopup(ing)}
                                      className={`text-xs px-2.5 py-1 rounded-full border ${ing ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                                      style={{ background: '#F8EDF0', color: '#7A3F4E', borderColor: '#E5B8C4' }}>
                                      {n}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {f.baseNotes && f.baseNotes.length > 0 && (
                            <div>
                              <p className="text-xs tracking-widest uppercase mb-1.5" style={{ color: '#6A5040' }}>Base</p>
                              <div className="flex flex-wrap gap-1.5">
                                {f.baseNotes.map((n, i) => {
                                  const ing = lookupIngredient(n);
                                  return (
                                    <button key={i} onClick={() => ing && setIngredientPopup(ing)}
                                      className={`text-xs px-2.5 py-1 rounded-full border ${ing ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                                      style={{ background: '#EDE8E2', color: '#4A3828', borderColor: '#C8B8A8' }}>
                                      {n}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {(!f.topNotes || f.topNotes.length === 0) &&
                           (!f.middleNotes || f.middleNotes.length === 0) &&
                           (!f.baseNotes || f.baseNotes.length === 0) &&
                           uniqueNotes.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {uniqueNotes.map((n, i) => {
                                const ing = lookupIngredient(n);
                                return (
                                  <button key={i} onClick={() => ing && setIngredientPopup(ing)}
                                    className={`text-xs bg-cream-dark text-stone px-2.5 py-1 rounded-full border border-cream-dark ${ing ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}>
                                    {n}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Scentd Collection */}
                      {(() => {
                        const total = (f.loveCt || 0) + (f.likeCt || 0) + (f.neutralCt || 0) + (f.dislikeCt || 0);
                        if (total === 0 && !f.wishlistCt) return null;
                        const pct = (n: number) => total > 0 ? Math.round((n / total) * 100) : 0;
                        return (
                          <div className="pt-3 border-t border-cream-dark">
                            <div className="flex items-baseline gap-2 mb-3">
                              <p className="text-xs tracking-widest uppercase text-stone-light">Scentd Collection</p>
                              {total > 0 && <p className="text-xs text-stone-light">· {total} {total === 1 ? 'member' : 'members'}</p>}
                            </div>
                            <div className="grid grid-cols-4 gap-2 mb-2">
                              {[
                                { label: 'All-Time Fave', count: f.loveCt || 0, symbol: <span>★</span> },
                                { label: 'Liked', count: f.likeCt || 0, symbol: <IconHeart size={11} strokeWidth={1.5} className="inline-block" /> },
                                { label: 'Neutral', count: f.neutralCt || 0, symbol: <span>~</span> },
                                { label: 'Disliked', count: f.dislikeCt || 0, symbol: <span>✕</span> },
                              ].map(stat => (
                                <div key={stat.label} className="bg-cream rounded-lg px-2 py-2.5 text-center">
                                  <p className="font-serif text-lg text-charcoal leading-none">{pct(stat.count)}%</p>
                                  <p className="text-xs text-stone-light mt-0.5 leading-tight">{stat.count} {stat.count === 1 ? 'member' : 'members'}</p>
                                  <p className="text-xs text-stone-light leading-tight flex items-center justify-center gap-0.5">{stat.symbol} {stat.label}</p>
                                </div>
                              ))}
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div className="bg-cream rounded-lg px-2 py-2.5 text-center">
                                <p className="font-serif text-lg text-charcoal leading-none">{pct(f.ownedCt || 0)}%</p>
                                <p className="text-xs text-stone-light mt-0.5 leading-tight">{f.ownedCt || 0} {(f.ownedCt || 0) === 1 ? 'member' : 'members'}</p>
                                <p className="text-xs text-stone-light leading-tight">Already own it</p>
                              </div>
                              <div className="bg-cream rounded-lg px-2 py-2.5 text-center">
                                <p className="font-serif text-lg text-charcoal leading-none">{pct(f.wantBottleCt || 0)}%</p>
                                <p className="text-xs text-stone-light mt-0.5 leading-tight">{f.wantBottleCt || 0} {(f.wantBottleCt || 0) === 1 ? 'member' : 'members'}</p>
                                <p className="text-xs text-stone-light leading-tight">Want a bottle</p>
                              </div>
                              <div className="bg-cream rounded-lg px-2 py-2.5 text-center">
                                <p className="font-serif text-lg text-charcoal leading-none">{f.wishlistCt || 0}</p>
                                <p className="text-xs text-stone-light mt-1 leading-tight">On wishlists</p>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>}
        </div>
      </div>

      {/* Ingredient popup */}
      {/* Bottle image lightbox */}
      {bottleLightbox && (
        <>
          <div className="fixed inset-0 bg-charcoal bg-opacity-60 z-40" onClick={() => setBottleLightbox(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white z-50 w-72 max-w-[88vw] rounded-2xl shadow-xl overflow-hidden">
            <button onClick={() => setBottleLightbox(null)} className="absolute top-3 right-3 w-7 h-7 rounded-full bg-cream flex items-center justify-center text-stone-light hover:text-charcoal z-10">
              <IconX size={13} />
            </button>
            <div className="w-full aspect-square bg-cream flex items-center justify-center">
              <img src={bottleLightbox.imageUrl} alt={bottleLightbox.name} className="w-full h-full object-contain p-4" />
            </div>
            <div className="px-5 py-4 text-center">
              <p className="font-serif text-lg text-charcoal">{bottleLightbox.name}</p>
              <p className="text-stone-light text-xs tracking-wide mt-0.5">{bottleLightbox.house}{bottleLightbox.year ? ` · ${bottleLightbox.year}` : ''}</p>
              {bottleLightbox.sourceUrl && (
                <a href={bottleLightbox.sourceUrl} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-stone-light hover:text-stone mt-2 inline-block tracking-wide underline underline-offset-2">
                  Source ↗
                </a>
              )}
            </div>
          </div>
        </>
      )}

      {ingredientPopup && (
        <>
          <div className="fixed inset-0 bg-charcoal bg-opacity-40 z-40" onClick={() => setIngredientPopup(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-cream z-50 w-80 rounded-xl max-w-[90vw] p-6 shadow-lg">
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="font-serif text-xl text-charcoal">{ingredientPopup.name}</p>
                <p className="text-xs tracking-widest uppercase text-stone-light">{ingredientPopup.category}</p>
              </div>
              <button onClick={() => setIngredientPopup(null)} className="text-stone-light hover:text-charcoal ml-4"><IconX size={16} /></button>
            </div>
            <p className="text-stone text-sm leading-relaxed">{ingredientPopup.description}</p>
          </div>
        </>
      )}

      {/* Journal view popup — shows all wear sessions for a journaled fragrance */}
      {journalViewFragrance && (() => {
        const entry = journalEntries.find(e => e.fragranceId === journalViewFragrance.fragranceId);
        const logs = entry ? [...(entry.wearLogs || (entry.wearLog ? [entry.wearLog] : []))].reverse() : [];
        const overallRating = entry?.rating || '';
        const ratingColor = overallRating === 'love' ? '#8CA87A' : overallRating === 'like' ? '#C4A882' : overallRating === 'neutral' ? '#A09890' : '#C4697A';
        const formatDate = (d: string) => { try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return d; } };
        return (
          <div
            className="fixed inset-0 z-50 flex items-end justify-center"
            style={{ background: 'rgba(42,37,32,0.5)' }}
            onClick={() => setJournalViewFragrance(null)}
          >
            <div
              className="w-full flex flex-col overflow-hidden"
              style={{ maxWidth: 430, maxHeight: '88vh', background: '#F5F0E8', borderRadius: '20px 20px 0 0' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Drag handle */}
              <div className="flex-shrink-0 flex justify-center pt-3 pb-1">
                <div style={{ width: 36, height: 4, background: '#D4CEBF', borderRadius: 2 }}/>
              </div>

              {/* Header */}
              <div className="flex justify-between items-start px-5 py-3 flex-shrink-0" style={{ borderBottom: '1px solid #EDE8DF' }}>
                <div>
                  <p className="font-serif text-xl text-charcoal leading-snug">{journalViewFragrance.name}</p>
                  <p className="text-xs text-stone-light mt-0.5">{journalViewFragrance.house}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: ratingColor, flexShrink: 0 }}/>
                    <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#2A2520' }}>{overallRating === 'love' ? 'ATF' : overallRating}</span>
                    <span className="text-xs text-stone-light">· {logs.length} {logs.length === 1 ? 'session' : 'sessions'}</span>
                  </div>
                </div>
                <button
                  onClick={() => setJournalViewFragrance(null)}
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 hover:opacity-70 transition-opacity"
                  style={{ border: '1px solid #EDE8DF', background: 'white', color: '#9E9688', fontSize: 14 }}
                >✕</button>
              </div>

              {/* Sessions */}
              <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
                {logs.length === 0 && (
                  <p className="text-sm text-stone-light text-center py-8">No sessions logged yet.</p>
                )}
                {logs.map((log: any, i: number) => {
                  const logRating = log.rating || '';
                  const logRatingColor = logRating === 'love' ? '#8CA87A' : logRating === 'like' ? '#C4A882' : logRating === 'neutral' ? '#A09890' : '#C4697A';
                  const chips = [
                    log.timeOfDay,
                    log.season,
                    ...(log.ownership || []).map((o: string) => o === 'bottle' ? 'Owns bottle' : o === 'sample' ? 'Has sample' : o === 'sold' ? 'No longer owns' : o === 'would_buy_again' ? 'Would buy again' : o),
                  ].filter(Boolean);
                  return (
                    <div key={log.logId || i} className="rounded-xl p-4" style={{ background: 'white', border: '1px solid #EDE8DF' }}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-stone-light">{formatDate(log.date)}</span>
                        <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: logRatingColor }}>{logRating === 'love' ? 'ATF' : logRating}</span>
                      </div>
                      {chips.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {chips.map((c: string, ci: number) => (
                            <span key={ci} className="text-xs px-2 py-0.5 rounded-full" style={{ border: '1px solid #EDE8DF', background: '#FAF6EF', color: '#8B7355', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{c}</span>
                          ))}
                        </div>
                      )}
                      {log.topNotesImpression && <div className="mb-2"><p className="text-xs tracking-widest uppercase text-stone-light mb-0.5">First impression</p><p className="text-sm text-charcoal leading-relaxed">{log.topNotesImpression}</p></div>}
                      {log.heartNotesImpression && <div className="mb-2"><p className="text-xs tracking-widest uppercase text-stone-light mb-0.5">After an hour</p><p className="text-sm text-charcoal leading-relaxed">{log.heartNotesImpression}</p></div>}
                      {log.baseNotesImpression && <div className="mb-2"><p className="text-xs tracking-widest uppercase text-stone-light mb-0.5">End of day</p><p className="text-sm text-charcoal leading-relaxed">{log.baseNotesImpression}</p></div>}
                      {log.notes && <p className="text-sm text-stone leading-relaxed mt-2 pt-2 italic" style={{ borderTop: '1px solid #F0EBE1' }}>"{log.notes}"</p>}
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-5 py-4" style={{ borderTop: '1px solid #EDE8DF' }}>
                <button
                  onClick={() => { setJournalViewFragrance(null); setJournalPresetRating(null); setJournalFragrance(journalViewFragrance); }}
                  className="w-full py-3.5 rounded-xl text-xs tracking-widest uppercase flex items-center justify-center gap-2 transition-colors"
                  style={{ background: '#2A2520', color: '#F5F0E8' }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Log new session
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Journal entry panel */}
      {journalFragrance && (
        <JournalEntryPanel
          fragrance={journalFragrance}
          userId={userId}
          authToken={authToken}
          existingEntry={journalEntries.find(e => e.fragranceId === journalFragrance.fragranceId)}
          presetRating={journalPresetRating || undefined}
          onClose={() => { setJournalFragrance(null); setJournalPresetRating(null); }}
          onSaved={() => {
            onJournalSaved();
            setJournalFragrance(null);
            setJournalPresetRating(null);
          }}
        />
      )}
    </>
  );
};

export default Discover;
